import { ExclamationCircleFilled } from '@ant-design/icons'
import { googlePlacesAdminAreaToSubdivision } from '@wagenow-ewa/common/dist/src/utils/countryUtils'
import { removeDiacritics } from '@wagenow-ewa/common/dist/src/utils/stringUtils'
import { Button, Col, Form, Row } from 'antd'
import { find, includes, map } from 'lodash'
import { FC, useEffect, useMemo } from 'react'
import CountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { change, Field, InjectedFormProps, reduxForm, WrappedFieldMetaProps } from 'redux-form'
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'
import AutoCompleteField from '../../../atoms/form/AutoCompleteField'
import DatePickerField from '../../../atoms/form/DatePickerField'
import IBANInputField from '../../../atoms/form/IBANInputField'
import InputField from '../../../atoms/form/InputField'
import PhoneNumberField from '../../../atoms/form/PhoneNumberField'
import SelectField from '../../../atoms/form/SelectField'
import SwitchField from '../../../atoms/form/SwitchField'
import { RootState } from '../../../redux'
import { FORM } from '../../../utils/enums'
import { getPhonePrefixByCountryCode } from '../../../utils/helpers'
import { getLocalizedUrls } from '../../../utils/languageUtils'
import validateWelcomeForm from './validateWelcomeForm'

const flag = (code: string) => <CountryFlag countryCode={code} svg style={{ width: '1.4em', height: '1.4em', borderRadius: '2px' }} />

type IWelcomeForm = {}

export interface IWelcomeFormValues {
	businessName?: string
	businessRegistrationNumber?: number
	firstName?: string
	lastName?: string
	email?: string
	bankAccountNumber?: string // Keep for backward compatibility
	// Individual bank account fields
	iban?: string
	bic?: string
	accountNumber?: string
	sortCode?: string
	phonePrefix?: string
	phoneNumber?: string
	birthday?: string
	birthNumber?: string
	street?: string
	houseNumber?: string
	city?: string
	zipCode?: string
	country?: string
	subdivision?: string | null
	consent?: boolean
	gdprConsent?: boolean
	employeeIdentifier?: string
}

const WelcomeForm: FC<InjectedFormProps<IWelcomeFormValues, IWelcomeForm> & IWelcomeForm> = ({ submitting, handleSubmit }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const COUNTRY_OPTIONS = useMemo(
		() => [
			{ value: 'al', label: t('loc:countries|Albania'), flagComponent: flag('AL') },
			{ value: 'ad', label: t('loc:countries|Andorra'), flagComponent: flag('AD') },
			{ value: 'at', label: t('loc:countries|Austria'), flagComponent: flag('AT') },
			{ value: 'by', label: t('loc:countries|Belarus'), flagComponent: flag('BY') },
			{ value: 'be', label: t('loc:countries|Belgium'), flagComponent: flag('BE') },
			{ value: 'ba', label: t('loc:countries|Bosnia and Herzegovina'), flagComponent: flag('BA') },
			{ value: 'bg', label: t('loc:countries|Bulgaria'), flagComponent: flag('BG') },
			{ value: 'hr', label: t('loc:countries|Croatia'), flagComponent: flag('HR') },
			{ value: 'cy', label: t('loc:countries|Cyprus'), flagComponent: flag('CY') },
			{ value: 'cz', label: t('loc:countries|Czech Republic'), flagComponent: flag('CZ') },
			{ value: 'dk', label: t('loc:countries|Denmark'), flagComponent: flag('DK') },
			{ value: 'ee', label: t('loc:countries|Estonia'), flagComponent: flag('EE') },
			{ value: 'fi', label: t('loc:countries|Finland'), flagComponent: flag('FI') },
			{ value: 'fr', label: t('loc:countries|France'), flagComponent: flag('FR') },
			{ value: 'de', label: t('loc:countries|Germany'), flagComponent: flag('DE') },
			{ value: 'gr', label: t('loc:countries|Greece'), flagComponent: flag('GR') },
			{ value: 'hu', label: t('loc:countries|Hungary'), flagComponent: flag('HU') },
			{ value: 'is', label: t('loc:countries|Iceland'), flagComponent: flag('IS') },
			{ value: 'ie', label: t('loc:countries|Ireland'), flagComponent: flag('IE') },
			{ value: 'it', label: t('loc:countries|Italy'), flagComponent: flag('IT') },
			{ value: 'lv', label: t('loc:countries|Latvia'), flagComponent: flag('LV') },
			{ value: 'li', label: t('loc:countries|Liechtenstein'), flagComponent: flag('LI') },
			{ value: 'lt', label: t('loc:countries|Lithuania'), flagComponent: flag('LT') },
			{ value: 'lu', label: t('loc:countries|Luxembourg'), flagComponent: flag('LU') },
			{ value: 'mt', label: t('loc:countries|Malta'), flagComponent: flag('MT') },
			{ value: 'md', label: t('loc:countries|Moldova'), flagComponent: flag('MD') },
			{ value: 'mc', label: t('loc:countries|Monaco'), flagComponent: flag('MC') },
			{ value: 'me', label: t('loc:countries|Montenegro'), flagComponent: flag('ME') },
			{ value: 'nl', label: t('loc:countries|Netherlands'), flagComponent: flag('NL') },
			{ value: 'mk', label: t('loc:countries|North Macedonia'), flagComponent: flag('MK') },
			{ value: 'no', label: t('loc:countries|Norway'), flagComponent: flag('NO') },
			{ value: 'pl', label: t('loc:countries|Poland'), flagComponent: flag('PL') },
			{ value: 'pt', label: t('loc:countries|Portugal'), flagComponent: flag('PT') },
			{ value: 'ro', label: t('loc:countries|Romania'), flagComponent: flag('RO') },
			{ value: 'rs', label: t('loc:countries|Serbia'), flagComponent: flag('RS') },
			{ value: 'sk', label: t('loc:countries|Slovakia'), flagComponent: flag('SK') },
			{ value: 'si', label: t('loc:countries|Slovenia'), flagComponent: flag('SI') },
			{ value: 'es', label: t('loc:countries|Spain'), flagComponent: flag('ES') },
			{ value: 'se', label: t('loc:countries|Sweden'), flagComponent: flag('SE') },
			{ value: 'ch', label: t('loc:countries|Switzerland'), flagComponent: flag('CH') },
			{ value: 'ua', label: t('loc:countries|Ukraine'), flagComponent: flag('UA') },
			{ value: 'gb', label: t('loc:countries|United Kingdom'), flagComponent: flag('GB') }
		],
		[t]
	)

	const SUBDIVISION_OPTIONS = useMemo(
		() => [
			{ value: 'GB-ENG', label: t('loc:countries|England'), flagComponent: flag('GB-ENG') },
			{ value: 'GB-SCT', label: t('loc:countries|Scotland'), flagComponent: flag('GB-SCT') },
			{ value: 'GB-WLS', label: t('loc:countries|Wales'), flagComponent: flag('GB-WLS') },
			{ value: 'GB-NIR', label: t('loc:countries|Northern Ireland'), flagComponent: flag('GB-NIR') }
		],
		[t]
	)
	const fromValues = useSelector((state: RootState) => state.form[FORM.ONBOARDING]?.values)

	const { data: onboarding } = useSelector((state: RootState) => state.onboarding)
	const isMobile = useMediaQuery({ maxWidth: 991 })

	// Google Places uses ISO 3166-1 alpha-2; normalize 'UK' → 'GB'
	const placesCountryCode = onboarding?.countryCode?.toUpperCase()

	const {
		ready,
		setValue,
		suggestions: { data },
		clearSuggestions
	} = usePlacesAutocomplete({
		debounce: 300,
		requestOptions: {
			types: ['address'],
			...(placesCountryCode ? { componentRestrictions: { country: placesCountryCode } } : {})
		}
	})

	const { termsUrl, privacyUrl } = getLocalizedUrls(onboarding?.language)

	const allowedFields = onboarding?.onboardingForm.additionalFields

	const businessNameField = find(allowedFields, ['fieldName', 'businessName'])
	const businessRegistrationNumberField = find(allowedFields, ['fieldName', 'businessRegistrationNumber'])
	const firstNameField = find(allowedFields, ['fieldName', 'firstName'])
	const lastNameField = find(allowedFields, ['fieldName', 'lastName'])
	const emailField = find(allowedFields, ['fieldName', 'email'])
	const birthdayField = find(allowedFields, ['fieldName', 'birthday'])
	const birthNumberField = find(allowedFields, ['fieldName', 'birthNumber'])
	const phoneNumberField = find(allowedFields, ['fieldName', 'phoneNumber'])
	const bankAccountNumberField = find(allowedFields, ['fieldName', 'bankAccountNumber'])
	const ibanField = find(allowedFields, ['fieldName', 'iban'])
	const bicField = find(allowedFields, ['fieldName', 'bic'])
	const accountNumberField = find(allowedFields, ['fieldName', 'accountNumber'])
	const sortCodeField = find(allowedFields, ['fieldName', 'sortCode'])
	const streetField = find(allowedFields, ['fieldName', 'street'])

	const houseNumberField = find(allowedFields, ['fieldName', 'houseNumber'])
	const cityField = find(allowedFields, ['fieldName', 'city'])
	const zipCodeField = find(allowedFields, ['fieldName', 'zipCode'])
	const countryField = find(allowedFields, ['fieldName', 'country'])
	const employeeIdentifierField = find(allowedFields, ['fieldName', 'employeeIdentifier'])
	const consentField = find(allowedFields, ['fieldName', 'consent'])
	const gdprConsentField = find(allowedFields, ['fieldName', 'gdprConsent'])

	const handleStreetChange = (value: string) => {
		setValue(value)
		dispatch(change(FORM.ONBOARDING, 'street', value || undefined))
	}

	const handleSelect = (_value: string, option: any) => {
		interface IAddress {
			long_name: string
			short_name: string
			types: string[]
		}

		// Use place_id for reliable geocoding — text-based geocoding frequently omits postal_code
		getGeocode({ placeId: option.placeId }).then((results) => {
			const components: IAddress[] = results[0]?.address_components ?? []
			const getComponent = (type: string) => find(components, (c: IAddress) => includes(c.types, type))

			const route = getComponent('route')
			const streetNumber = getComponent('street_number')
			const premise = getComponent('premise')
			const subpremise = getComponent('subpremise')
			const postalCode = getComponent('postal_code')
			// City: locality = primary (Bratislava, Prague); postal_town = UK (London); sublocality = last resort
			const locality = getComponent('locality')
			const postalTown = getComponent('postal_town')
			const sublocality = getComponent('sublocality_level_1') || getComponent('sublocality')
			const country = getComponent('country')
			// UK-only: administrative_area_level_1 gives the constituent country (England, Scotland, etc.)
			const adminArea = getComponent('administrative_area_level_1')

			if (postalCode) {
				dispatch(change(FORM.ONBOARDING, 'zipCode', postalCode.long_name))
			}

			const cityName = locality?.long_name || postalTown?.long_name || sublocality?.long_name
			if (cityName) {
				dispatch(change(FORM.ONBOARDING, 'city', cityName))
			}

			// House number: named building (premise) takes priority over street_number
			if (premise) {
				dispatch(change(FORM.ONBOARDING, 'houseNumber', subpremise ? `${premise.long_name}/${subpremise.long_name}` : premise.long_name))
			} else if (streetNumber) {
				dispatch(change(FORM.ONBOARDING, 'houseNumber', streetNumber.long_name))
			}

			// Street is the route name only — house number is separate above
			if (route) {
				dispatch(change(FORM.ONBOARDING, 'street', route.long_name))
			}

			if (country) {
				const internalCode = country.short_name.toLowerCase()
				dispatch(change(FORM.ONBOARDING, 'country', internalCode))

				// For UK addresses, extract the constituent country as an ISO 3166-2 subdivision code
				const subdivision = googlePlacesAdminAreaToSubdivision(adminArea?.long_name, internalCode)
				dispatch(change(FORM.ONBOARDING, 'subdivision', subdivision ?? null))
			}

			clearSuggestions()
		})
	}

	useEffect(() => {
		const phonePrefix = getPhonePrefixByCountryCode(onboarding?.countryCode)
		dispatch(change(FORM.ONBOARDING, 'phonePrefix', phonePrefix))
	}, [dispatch, onboarding?.countryCode])

	useEffect(() => {
		if (!onboarding?.countryCode) return
		const normalized = onboarding.countryCode.toLowerCase()
		if (COUNTRY_OPTIONS.some((opt) => opt.value === normalized)) {
			dispatch(change(FORM.ONBOARDING, 'country', normalized))
		}
	}, [dispatch, onboarding?.countryCode])

	useEffect(() => {
		if (fromValues?.country !== 'gb') {
			dispatch(change(FORM.ONBOARDING, 'subdivision', null))
		}
	}, [dispatch, fromValues?.country])

	const submitDisabled = () => {
		const unfilled = []
		if (fromValues?.businessName === undefined && businessNameField?.isRequired) {
			unfilled.push(t('loc:general|Company name'))
		}
		if (fromValues?.businessRegistrationNumber === undefined && businessRegistrationNumberField?.isRequired) {
			unfilled.push(t('loc:general|Company id'))
		}
		if (fromValues?.firstName === undefined && firstNameField?.isRequired) {
			unfilled.push(t('loc:general|First name'))
		}
		if (fromValues?.lastName === undefined && lastNameField?.isRequired) {
			unfilled.push(t('loc:general|Last name'))
		}
		if (fromValues?.employeeIdentifier === undefined && employeeIdentifierField?.isRequired) {
			unfilled.push(t('loc:general|Employee identifier'))
		}
		if (fromValues?.email === undefined && emailField?.isRequired) {
			unfilled.push(t('loc:general|Email'))
		}
		if (fromValues?.birthday === undefined && birthdayField?.isRequired) {
			unfilled.push(t('loc:general|Birth day'))
		}
		if (fromValues?.birthNumber === undefined && birthNumberField?.isRequired) {
			unfilled.push(t('loc:general|Birth Number'))
		}
		if (fromValues?.phoneNumber === undefined && phoneNumberField?.isRequired) {
			unfilled.push(t('loc:general|Phone number'))
		}
		// Bank account validation - simplified frontend validation
		// Backend handles the complex discriminated union logic with IBAN priority
		if (fromValues?.bankAccountNumber === undefined && bankAccountNumberField?.isRequired) {
			unfilled.push(t('loc:general|IBAN'))
		}
		if (fromValues?.iban === undefined && ibanField?.isRequired) {
			unfilled.push(t('loc:general|IBAN'))
		}
		if (fromValues?.bic === undefined && bicField?.isRequired) {
			unfilled.push(t('loc:general|BIC'))
		}
		if (fromValues?.accountNumber === undefined && accountNumberField?.isRequired) {
			unfilled.push(t('loc:general|Account Number'))
		}
		if (fromValues?.sortCode === undefined && sortCodeField?.isRequired) {
			unfilled.push(t('loc:general|Sort Code'))
		}
		if (fromValues?.street === undefined && streetField?.isRequired) {
			unfilled.push(t('loc:general|Street'))
		}
		// Check house number requirement: either from houseNumberField config or streetField fallback
		const isHouseNumberRequired = houseNumberField?.isRequired || (streetField?.isRequired && !houseNumberField)
		if (fromValues?.houseNumber === undefined && isHouseNumberRequired) {
			unfilled.push(t('loc:general|House number'))
		}
		if (fromValues?.city === undefined && streetField?.isRequired) {
			unfilled.push(t('loc:general|City'))
		}
		if (fromValues?.zipCode === undefined && streetField?.isRequired) {
			unfilled.push(t('loc:general|Zip Code'))
		}
		if (fromValues?.country === undefined && countryField?.isRequired) {
			unfilled.push(t('loc:general|Country'))
		}
		if (countryField && fromValues?.country === 'gb' && !fromValues?.subdivision) {
			unfilled.push(t('loc:general|Subdivision'))
		}
		if (!fromValues?.consent) {
			unfilled.push(t('loc:general|consent'))
		}
		if (!fromValues?.gdprConsent) {
			unfilled.push(t('loc:general|privacy policy'))
		}
		return {
			disabled: unfilled.length !== 0,
			error: `${t('loc:general|Please fill up required fields')}: ${unfilled.join(', ')}`
		}
	}

	return (
		<Form onSubmitCapture={handleSubmit}>
			<h3 className={'text-22 font-700 mb-4 mt-[30px]'}>{t('loc:general|Personal data')}</h3>
			<div className='flex flex-row gap-5 bg-red-light py-8 px-6 rounded-md shadow-md w-100 mb-6'>
				<ExclamationCircleFilled className='text-28 text-error' />
				<div className='text-align-left'>
					<div className='text-16 font-500'>{t('loc:general|Please use your real name')}.</div>
					<div className='text-16 text-gray-500'>
						{t('loc:general|Contracts are generated based on it and matching with the attendance system takes place')}.
					</div>
				</div>
			</div>
			<Row justify={'space-between'}>
				<Col span={isMobile ? 24 : 12}>
					{businessNameField && (
						<Field
							name={'businessName'}
							component={InputField}
							label={businessNameField?.label}
							placeholder={businessNameField?.placeholder}
							required={businessNameField?.isRequired}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</Col>
				<Col span={isMobile ? 24 : 12}>
					{businessRegistrationNumberField && (
						<Field
							name={'businessRegistrationNumber'}
							component={InputField}
							label={businessRegistrationNumberField?.label}
							placeholder={businessRegistrationNumberField?.placeholder}
							required={businessRegistrationNumberField?.isRequired}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</Col>
			</Row>

			<Row justify={'space-between'} gutter={16}>
				<Col span={isMobile ? 24 : 12}>
					{firstNameField && (
						<Field
							name={'firstName'}
							component={InputField}
							label={firstNameField.label}
							placeholder={firstNameField.placeholder}
							required={firstNameField.isRequired}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</Col>
				<Col span={isMobile ? 24 : 12}>
					{lastNameField && (
						<Field
							name={'lastName'}
							component={InputField}
							label={lastNameField.label}
							placeholder={lastNameField.placeholder}
							required={lastNameField.isRequired}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</Col>
			</Row>

			{employeeIdentifierField && (
				<Field
					name={'employeeIdentifier'}
					component={InputField}
					label={employeeIdentifierField.label}
					placeholder={employeeIdentifierField.placeholder}
					required={employeeIdentifierField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{emailField && (
				<Field
					name={'email'}
					component={InputField}
					label={emailField.label}
					placeholder={emailField.placeholder}
					required={emailField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{birthdayField && (
				<Field
					name={'birthday'}
					component={DatePickerField}
					label={birthdayField.label}
					placeholder={birthdayField.placeholder}
					required={birthdayField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
					startWithYear={true}
				/>
			)}

			{birthNumberField && (
				<Field
					name={'birthNumber'}
					component={InputField}
					label={birthNumberField.label}
					placeholder={birthNumberField.placeholder}
					required={birthNumberField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{phoneNumberField && (
				<Field
					name={'phoneNumber'}
					component={PhoneNumberField}
					label={phoneNumberField.label}
					placeholder={phoneNumberField.placeholder}
					required={phoneNumberField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{/* Bank Account Fields - Hybrid approach for backward compatibility */}
			{bankAccountNumberField && (
				<Field
					name={'bankAccountNumber'}
					component={IBANInputField}
					label={bankAccountNumberField.label}
					placeholder={bankAccountNumberField.placeholder}
					required={bankAccountNumberField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{/* New Bank Account Fields - Transformed into discriminated union in WelcomeFormPage.tsx */}
			{ibanField && (
				<Field
					name={'iban'}
					component={IBANInputField}
					label={ibanField.label}
					placeholder={ibanField.placeholder}
					required={ibanField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{/* UK Bank Account Fields */}
			{(accountNumberField || sortCodeField || bicField) && (
				<>
					<Row justify={'space-between'} gutter={16}>
						<Col span={isMobile ? 24 : 8}>
							{sortCodeField && (
								<Field
									name={'sortCode'}
									component={InputField}
									label={sortCodeField.label}
									placeholder={sortCodeField.placeholder}
									required={sortCodeField.isRequired}
									className={'mb-4'}
									readOnly={submitting}
								/>
							)}
						</Col>
						<Col span={isMobile ? 24 : 16}>
							{accountNumberField && (
								<Field
									name={'accountNumber'}
									component={InputField}
									label={accountNumberField.label}
									placeholder={accountNumberField.placeholder}
									required={accountNumberField.isRequired}
									className={'mb-4'}
									readOnly={submitting}
								/>
							)}
						</Col>
					</Row>
					{bicField && (
						<Field
							name={'bic'}
							component={InputField}
							label={bicField.label}
							placeholder={bicField.placeholder}
							required={bicField.isRequired}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</>
			)}

			<h3 className={'text-18 font-700 mb-4 mt-[30px]'}>{t('loc:general|Address')}</h3>

			<Row justify={'space-between'} gutter={16}>
				<Col span={isMobile ? 24 : 12}>
					{streetField && (
						<Field
							name={'street'}
							component={AutoCompleteField}
							label={streetField.label}
							placeholder={streetField.placeholder}
							required={streetField.isRequired}
							className={'mb-4'}
							disabled={submitting || !ready}
							onValueChange={handleStreetChange}
							onOptionSelect={handleSelect}
							options={map(data || [], (item: any) => ({
								value: removeDiacritics(item.description),
								label: item.description,
								placeId: item.place_id
							}))}
						/>
					)}
				</Col>
				<Col span={isMobile ? 24 : 12}>
					{(houseNumberField || streetField) && (
						<Field
							name={'houseNumber'}
							component={InputField}
							label={houseNumberField?.label || t('loc:general|HouseNumber')}
							placeholder={houseNumberField?.placeholder || t('loc:general|type house number')}
							required={houseNumberField?.isRequired || (streetField && !houseNumberField)}
							className={'mb-4'}
							readOnly={submitting}
						/>
					)}
				</Col>
			</Row>

			{cityField && (
				<Field
					name={'city'}
					component={InputField}
					label={cityField.label}
					placeholder={cityField.placeholder}
					required={cityField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{zipCodeField && (
				<Field
					name={'zipCode'}
					component={InputField}
					label={zipCodeField.label}
					placeholder={zipCodeField.placeholder}
					required={zipCodeField.isRequired}
					className={'mb-4'}
					readOnly={submitting}
				/>
			)}

			{countryField && (
				<Field
					name={'country'}
					component={SelectField}
					label={countryField.label}
					placeholder={countryField.placeholder}
					required={countryField.isRequired}
					className={'mb-4'}
					disabled={submitting}
					options={COUNTRY_OPTIONS}
					customOptions
					showSearch
					optionFilterProp={'label'}
				/>
			)}

			{countryField && fromValues?.country === 'gb' && (
				<Field
					name={'subdivision'}
					component={SelectField}
					label={t('loc:general|Subdivision')}
					placeholder={t('loc:general|Select subdivision')}
					required
					className={'mb-4'}
					disabled={submitting}
					options={SUBDIVISION_OPTIONS}
					customOptions
				/>
			)}
			<div className={'mb-6 mt-8'}>
				<Row justify={'start'} align={'middle'} className={'ant-row-no-wrap'}>
					<Col>
						<Field name={'consent'} component={SwitchField} required={consentField?.isRequired} showErrorMessage={false} />
					</Col>
					<Col>
						<span className={'inline-block text-13 break-words ml-4'}>
							{`${t('loc:general|I agree with')} `}
							<a href={termsUrl} className={'underline'} target={'_blank'} rel={'noopener noreferrer'}>
								{t('loc:general|with Terms and Conditions')}
							</a>
						</span>
					</Col>
				</Row>
				<Field
					name={'consent'}
					component={({ meta }: { meta: WrappedFieldMetaProps }) =>
						meta.error && meta.touched ? (
							<Row justify='start' className='mb-2'>
								<Col>
									<span className='form-field-message'>{meta.error}</span>
								</Col>
							</Row>
						) : (
							<Row justify='start' className='mb-2'>
								<Col>
									<span className='form-field-message'>&nbsp;</span>
								</Col>
							</Row>
						)
					}
				/>
			</div>
			<div className={'mb-6'}>
				<Row justify={'start'} align={'middle'} className={'ant-row-no-wrap'}>
					<Col>
						<Field name={'gdprConsent'} component={SwitchField} required={gdprConsentField?.isRequired} showErrorMessage={false} />
					</Col>
					<Col>
						<span className={'inline-block text-13 break-words ml-4'}>
							{`${t('loc:general|I agree with')} `}
							<a href={privacyUrl} className={'underline'} target={'_blank'} rel={'noopener noreferrer'}>
								{t('loc:general|with processing of personal data')}
							</a>
						</span>
					</Col>
				</Row>
				<Field
					name={'gdprConsent'}
					component={({ meta }: { meta: WrappedFieldMetaProps }) =>
						meta.error && meta.touched ? (
							<Row justify='start' className='mb-2'>
								<Col>
									<span className='form-field-message'>{meta.error}</span>
								</Col>
							</Row>
						) : (
							<Row justify='start' className='mb-2'>
								<Col>
									<span className='form-field-message'>&nbsp;</span>
								</Col>
							</Row>
						)
					}
				/>
			</div>
			<div className={'flex flex-col items-center gap-y-2 lg:gap-y-4 mt-10'}>
				<Button
					className={'max-w-[340px]'}
					type={'primary'}
					shape={'round'}
					block={true}
					htmlType={'submit'}
					loading={submitting}
					disabled={submitDisabled().disabled}
				>
					{t('loc:general|Submit')}
				</Button>
				{submitDisabled().disabled && (
					<p className={'form-field-message'} style={{ color: 'red' }}>
						{submitDisabled().error}
					</p>
				)}
			</div>
		</Form>
	)
}

export default reduxForm<IWelcomeFormValues, IWelcomeForm>({
	form: FORM.ONBOARDING,
	validate: validateWelcomeForm,
	touchOnChange: true,
	destroyOnUnmount: true,
	forceUnregisterOnUnmount: true
})(WelcomeForm)
